import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Skeleton, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { CalloutBanner } from '@components/CalloutBanner';
import { ListPagination } from '@components/ListPagination';
import { type SingularKeys, useTrans } from '@libs/i18n';
import {
  ArrowCounterClockwiseIcon,
  ArrowRightIcon,
  ClockIcon,
  InfoIcon,
  PulseIcon,
} from '@phosphor-icons/react';

import { useAssessmentProgress } from '../api/assessment-progress-api';
import { ActivityTimeline } from '../components/RunProgress/ActivityTimeline';
import { DocumentListCard } from '../components/RunProgress/DocumentList';
import {
  type CounterCell,
  FrozenNote,
  HeroPill,
  NoProgressTrack,
  RunCounters,
  RunHero,
  StageBar,
} from '../components/RunProgress/RunProgressParts';
import {
  buildStageSegments,
  deriveRunView,
  formatElapsed,
  groupDocumentsByDomain,
  isRunActive,
  minutesSince,
  resolveCounters,
  type RunView,
  STAGE_LABEL_KEYS,
  type StageSegment,
} from '../utils/run-progress';

/**
 * Documents per page.
 *
 * Chosen against the polling interval rather than the viewport: this screen
 * re-fetches every five seconds for up to 45 minutes, so an unpaged 200-document
 * run would re-download all 200 rows around 540 times to render the handful that
 * changed. Twenty covers a typical run in one page, so the pager stays hidden
 * for most people.
 */
const DOCUMENTS_PER_PAGE = 20;

/**
 * The run screen — where a submitted assessment lands.
 *
 * One route rather than one per state: queued, reading, grading, stalled, silent
 * and finished are all states of the same run, the query self-polls, and the
 * content swaps underneath. A user who leaves and comes back lands on whatever
 * is true now.
 */
export const AssessmentProgressPage: FC = observer(() => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();
  const { t } = useTrans();
  const theme = useTheme();

  /** One exit from the run, shared by the hero and the error branch. */
  const backToList = () => navigate(routes.assessmentList());

  const [page, setPage] = useState(1);
  const { data, isLoading, isError, isFetching, refetch } = useAssessmentProgress(assessmentId, {
    page,
    limit: DOCUMENTS_PER_PAGE,
  });

  // Clamped rather than reset, and only once the page has actually run off the
  // end. Documents do not appear mid-run — the scope is fixed at submission —
  // but a document deleted underneath a reader who has paged ahead would
  // otherwise strand them on an empty page, because the server answers a page
  // past the end with no rows rather than with the last page.
  const totalDocumentPages = data?.documentsMeta?.totalPages;
  useEffect(() => {
    if (totalDocumentPages !== undefined && page > totalDocumentPages) {
      setPage(Math.max(1, totalDocumentPages));
    }
  }, [page, totalDocumentPages]);

  // Ticks only while a run can still change, so a finished run's elapsed pill
  // stops rather than counting up forever. Same predicate the poller uses to
  // decide when to stop fetching.
  const now = useTicker(data ? isRunActive(data.status) : false);

  /**
   * The server's clock, advanced locally between polls.
   *
   * Aging `lastEventAt` against the BROWSER's clock is what would let a machine
   * an hour fast declare a healthy run stalled — and a machine an hour slow hide
   * a real stall. The endpoint returns `serverTime` for exactly this, and the
   * local tick only fills the gap between two polls.
   */
  const serverNow = useMemo(() => {
    if (!data?.serverTime) return now;
    const server = new Date(data.serverTime).getTime();
    return Number.isNaN(server) ? now : server;
  }, [data?.serverTime, now]);

  const view: RunView = useMemo(
    () => (data ? deriveRunView(data, serverNow) : 'queued'),
    [data, serverNow],
  );

  const segments = useMemo(() => buildStageSegments(data?.snapshot ?? null), [data?.snapshot]);
  const groups = useMemo(() => groupDocumentsByDomain(data?.documents ?? []), [data?.documents]);

  /**
   * Documents in scope for the WHOLE run.
   *
   * `documents.length` is the last resort rather than the obvious answer,
   * because it is one page — reading the total off it would make the figure
   * shrink to twenty on any run larger than that.
   */
  const documentsTotal =
    data?.snapshot?.documentsTotal ??
    data?.documentsMeta?.totalCount ??
    data?.documents.length ??
    0;

  const counters = useMemo(
    () =>
      resolveCounters(
        data?.snapshot ?? null,
        data?.documents ?? [],
        view,
        // The rows are a page, so the fold-the-rows fallback must not run.
        data?.documentsMeta != null,
        documentsTotal,
      ),
    [data?.snapshot, data?.documents, data?.documentsMeta, view, documentsTotal],
  );

  if (isLoading && !data) return <RunProgressSkeleton />;

  if (isError || !data) {
    return (
      <Shell>
        <Body>
          <CalloutBanner tone="error" title={t('app.assessment.progress.error.title')}>
            {t('app.assessment.progress.error.body')}
          </CalloutBanner>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="primary" onClick={() => void refetch()}>
              {t('app.assessment.progress.error.retry')}
            </Button>
            <Button variant="tertiary" onClick={backToList}>
              {t('app.assessment.progress.back')}
            </Button>
          </Box>
        </Body>
      </Shell>
    );
  }

  const elapsedFrom = data.snapshot?.firstEventAt ?? null;
  const elapsed = elapsedFrom ? minutesSince(elapsedFrom, serverNow) : 0;
  const sinceUpdate = data.snapshot ? minutesSince(data.snapshot.lastEventAt, serverNow) : 0;

  const hero = heroCopy(view, {
    assessed: counters.assessed,
    missing: counters.notSupplied,
    minutes: view === 'stalled' ? sinceUpdate : elapsed,
  });

  const counterCells: CounterCell[] = [
    {
      label: t('app.assessment.progress.counter.queued'),
      value: fmt(counters.queued, view),
      color: theme.palette.black[400],
    },
    {
      label: t('app.assessment.progress.counter.reading'),
      value: fmt(counters.reading, view),
      color: theme.palette.accent.blue,
    },
    {
      label: t('app.assessment.progress.counter.grading'),
      value: fmt(counters.grading, view),
      color: theme.palette.ocean[700],
    },
    {
      label: t('app.assessment.progress.counter.assessed'),
      value: fmt(counters.assessed, view),
      color: theme.palette.accent.green,
    },
    {
      label: t('app.assessment.progress.counter.failed'),
      value: fmt(counters.failed, view),
      color: theme.palette.error.main,
    },
    // No "not yet supplied" cell, and no missing-documents banner below. Every
    // counter on this row is a statement about the RUN — documents it has read,
    // is grading, has finished — and an expected-but-absent document is a fact
    // about the submission that the run will never touch. Mixing the two put a
    // number nobody could act on next to four that move, and read as a fault in
    // a run that has none. The honesty it existed for is carried by the panel at
    // the foot of the page, which already says the final maturity counts
    // documents that were not supplied.
  ];

  return (
    <Shell>
      <RunHero
        view={view}
        title={t(hero.title)}
        body={t(hero.body, hero.values)}
        onBack={backToList}
        trailing={
          <>
            <HeroPill
              icon={ClockIcon}
              label={t('app.assessment.progress.elapsed')}
              value={formatElapsed(elapsed)}
            />
            <HeroPill
              icon={PulseIcon}
              label={t('app.assessment.progress.lastUpdated')}
              value={data.snapshot ? `${sinceUpdate}m` : '—'}
              tone={view === 'stalled' ? 'warning' : 'neutral'}
            />
            {view === 'done' && (
              <Button
                variant="primary"
                onClick={() => navigate(routes.assessmentOverview(data.assessmentId))}
                endIcon={<ArrowRightIcon size={16} />}
              >
                {t('app.assessment.progress.viewReport')}
              </Button>
            )}
            {view === 'stalled' && (
              <Button
                variant="tertiary"
                onClick={() => navigate(routes.assessmentOverview(data.assessmentId))}
                startIcon={<ArrowCounterClockwiseIcon size={15} />}
              >
                {t('app.assessment.progress.restart')}
              </Button>
            )}
          </>
        }
      >
        {data.reporting ? (
          <StageBar
            segments={segments}
            currentStageLabel={
              data.snapshot?.currentStageKey
                ? t(STAGE_LABEL_KEYS[data.snapshot.currentStageKey])
                : undefined
            }
            countedLabel={
              documentsTotal > 0
                ? t('app.assessment.progress.countedOf', {
                    done: counters.assessed,
                    total: documentsTotal,
                  })
                : undefined
            }
            captionFor={(segment) => stageCaption(segment, t)}
          />
        ) : (
          <NoProgressTrack tone={view === 'starting' || view === 'queued' ? 'pending' : 'nodata'} />
        )}

        {view === 'stalled' && <FrozenNote />}
      </RunHero>

      <Body>
        <RunCounters counters={counterCells} />

        {counters.failed > 0 && (
          <CalloutBanner
            tone="error"
            density="compact"
            title={t('app.assessment.progress.failedTitle', { failed: counters.failed })}
          >
            {t('app.assessment.progress.failedBody')}
          </CalloutBanner>
        )}

        <Box
          sx={{
            display: 'grid',
            gap: 2.5,
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2.05fr) minmax(300px, 1fr)' },
            alignItems: 'start',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
            <DocumentListCard
              groups={groups}
              runView={view}
              meta={t('app.assessment.progress.documentCount', { total: documentsTotal })}
              footerNote={t('app.assessment.progress.countedOf', {
                done: counters.assessed,
                total: documentsTotal,
              })}
            />
            {data.documentsMeta && data.documentsMeta.totalPages > 1 && (
              <ListPagination
                page={data.documentsMeta.currentPage}
                totalPages={data.documentsMeta.totalPages}
                hasPrevPage={data.documentsMeta.hasPrevPage}
                hasNextPage={data.documentsMeta.hasNextPage}
                onPageChange={setPage}
                isLoading={isFetching}
              />
            )}
          </Box>

          <ActivityTimeline events={data.events} firstEventAt={data.snapshot?.firstEventAt} />
        </Box>

        {/*
        The panel that keeps the gap counts above from being read as a score.
        Maturity is weighted, spans domains, and counts documents that were
        never supplied — three reasons the numbers on this screen cannot be
        added up into it, and the reason no total appears anywhere here.
      */}
        <CalloutBanner
          tone="neutral"
          icon={InfoIcon}
          title={t('app.assessment.progress.noScoreTitle')}
        >
          {t('app.assessment.progress.noScoreBody')}
        </CalloutBanner>
      </Body>
    </Shell>
  );
});

// =============================================================================
// Copy selection
// =============================================================================

function heroCopy(
  view: RunView,
  values: { assessed: number; missing: number; minutes: number },
): { title: SingularKeys; body: SingularKeys; values: Record<string, number> } {
  switch (view) {
    case 'done':
      return {
        title: 'app.assessment.progress.hero.done.title',
        body:
          values.missing > 0
            ? 'app.assessment.progress.hero.done.body'
            : 'app.assessment.progress.hero.doneNoMissing.body',
        values: { assessed: values.assessed, missing: values.missing },
      };
    case 'runFailed':
      return {
        title: 'app.assessment.progress.hero.runFailed.title',
        body: 'app.assessment.progress.hero.runFailed.body',
        values: {},
      };
    case 'stalled':
      return {
        title: 'app.assessment.progress.hero.stalled.title',
        body: 'app.assessment.progress.hero.stalled.body',
        values: { minutes: values.minutes, counted: values.assessed },
      };
    case 'unknown':
      return {
        title: 'app.assessment.progress.hero.unknown.title',
        body: 'app.assessment.progress.hero.unknown.body',
        values: { minutes: values.minutes },
      };
    case 'queued':
      return {
        title: 'app.assessment.progress.hero.queued.title',
        body: 'app.assessment.progress.hero.queued.body',
        values: {},
      };
    case 'starting':
      return {
        title: 'app.assessment.progress.hero.starting.title',
        body: 'app.assessment.progress.hero.starting.body',
        values: {},
      };
    case 'grading':
      return {
        title: 'app.assessment.progress.hero.grading.title',
        body: 'app.assessment.progress.hero.grading.body',
        values: {},
      };
    default:
      return {
        title: 'app.assessment.progress.hero.early.title',
        body: 'app.assessment.progress.hero.early.body',
        values: {},
      };
  }
}

/**
 * The caption under one segment.
 *
 * Counts only, never a percentage: "4 of 17 · 3 in flight" is checkable against
 * the rows below it, where "24%" is not checkable against anything.
 */
function stageCaption(
  segment: StageSegment,
  t: (key: SingularKeys, options?: Record<string, unknown>) => string,
): string {
  if (segment.total == null || segment.total === 0) {
    if (segment.done === 100) return t('app.assessment.progress.stageDone');
    return '';
  }

  const counted = segment.counted ?? 0;
  if (segment.inFlightCount) {
    return t('app.assessment.progress.countedInFlight', {
      done: counted,
      total: segment.total,
      flight: segment.inFlightCount,
    });
  }

  return t('app.assessment.progress.countedOf', { done: counted, total: segment.total });
}

/** A run reporting nothing shows dashes, not zeroes. Zero is a claim. */
function fmt(value: number, view: RunView): string {
  return view === 'unknown' ? '—' : String(value);
}

// =============================================================================
// Shell + loading
// =============================================================================

/**
 * `AppLayout`'s content area is a fixed-height `overflow: hidden` box — it
 * expects whatever it renders to scroll internally rather than push the
 * layout's own height. The run screen easily exceeds one viewport (hero +
 * stage bar + counters + a 20-row document table + timeline), so this
 * establishes that scroll region itself instead of being silently clipped.
 */
/**
 * Page frame — one white card that FILLS the content area, flush to its edges.
 *
 * `AppLayout` hands each page a fixed-height `overflow: hidden` region with its
 * own margin, and every list screen (assessments home, remediation list) fills
 * that region with a single rounded paper card and scrolls inside it. A centred
 * max-width column or a grey ground with floating cards reads as a page that
 * has not settled into its space.
 *
 * No `maxWidth`: capping it crowds the stage bar and the document table's four
 * columns while the window sits half empty — the same reason the remediation
 * list dropped its cap.
 */
const Shell: FC<{ children: React.ReactNode }> = observer(({ children }) => (
  <Box
    sx={{
      height: '100%',
      overflowY: 'auto',
      backgroundColor: 'background.paper',
      borderRadius: 4,
    }}
  >
    {children}
  </Box>
));

/** Padded body below the hero, which supplies its own edge padding. */
const Body: FC<{ children: React.ReactNode }> = observer(({ children }) => (
  <Box
    sx={{
      px: { xs: 2, sm: 3 },
      py: 3,
      display: 'flex',
      flexDirection: 'column',
      gap: 2.5,
    }}
  >
    {children}
  </Box>
));

const RunProgressSkeleton: FC = observer(() => (
  <Shell>
    <Body>
      <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />
      <Skeleton variant="rounded" height={90} sx={{ borderRadius: 2 }} />
      <Skeleton variant="rounded" height={420} sx={{ borderRadius: 3 }} />
    </Body>
  </Shell>
));

/**
 * A once-a-minute clock, so the elapsed pill advances between polls.
 *
 * Minute resolution on purpose — the pill is rendered in whole minutes, so a
 * faster tick would re-render the tree for a value that cannot have changed.
 */
function useTicker(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, [active]);

  return now;
}
